/**
 * Shared types, constants, and helpers for the CSV import summary view.
 */

import {
  AlertCircle,
  LayersThree01,
  Plus,
  RefreshCw01,
  Users01,
} from '@untitledui/icons';
import type { FC, SVGProps } from 'react';

import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '@/lib/crm/hooks/usePropertySchema';
import type { AnalyzedRow, CsvImport } from '@/lib/csv-import/api';
import { fetchWithDefaults } from '@/lib/http/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Categories surfaced in the review UI.
 *
 * - `create` — new records that will be created in the CRM.
 * - `update` — existing CRM records that will be updated.
 * - `batch_dedup` — rows in the import that were collapsed into an earlier
 *   row of the same import because they share the same name and no
 *   unique-value property (e.g. domain, email) was mapped. These rows
 *   carry `action="update"` on the wire so the executor can write their
 *   properties onto the collapsed entity, but they are NOT updates to an
 *   existing CRM record — surfacing them as "Updates" is misleading.
 * - `invalid` — rows that failed validation and will be skipped.
 * - `ambiguous` — rows where multiple existing CRM records matched the
 *   configured match columns; not imported. The user resolves by adding a
 *   more specific match column.
 */
export type RowCategory =
  | 'create'
  | 'update'
  | 'batch_dedup'
  | 'invalid'
  | 'ambiguous';

export interface ImportTotals {
  create: number;
  update: number;
  batch_dedup: number;
  invalid: number;
  ambiguous: number;
  conflicts: number;
  total: number;
}

export function computeImportTotals(
  analysisResult: CsvImport['analysis_result']
): ImportTotals {
  const result: ImportTotals = {
    create: 0,
    update: 0,
    batch_dedup: 0,
    invalid: 0,
    ambiguous: 0,
    conflicts: 0,
    total: 0,
  };
  for (const summary of Object.values(analysisResult?.file_summaries ?? {})) {
    result.create += summary.new_rows ?? 0;
    result.update += summary.update_rows ?? 0;
    result.batch_dedup += summary.batch_dedup_rows ?? 0;
    result.invalid += summary.invalid_rows ?? summary.skip_rows ?? 0;
    result.ambiguous += summary.ambiguous_rows ?? 0;
    result.conflicts += summary.conflict_rows ?? 0;
  }
  result.total =
    result.create +
    result.update +
    result.batch_dedup +
    result.invalid +
    result.ambiguous;
  return result;
}

const EMPTY_FILENAMES: string[] = [];

/**
 * Filenames of files whose analysis flagged `requires_match_columns` — the
 * backend will reject approval while any of these are set, so the Approve
 * button must stay disabled until the user maps a unique match column.
 * Returns a stable empty-array reference when none apply.
 */
export function getMatchColumnsRequiredFilenames(
  analysisResult: CsvImport['analysis_result']
): string[] {
  const filenames = Object.values(analysisResult?.file_summaries ?? {})
    .filter(summary => summary.requires_match_columns)
    .map(summary => summary.filename);
  return filenames.length > 0 ? filenames : EMPTY_FILENAMES;
}

/**
 * Whether the `csv_imports.reimport_block_enabled` kill switch was on at analysis
 * time. Defaults to `true` (the pre-feature/unset default, matching backend
 * fail-closed semantics) unless every flagged file summary explicitly reports the
 * switch as off — an operator turning the switch off must unblock Approve even
 * though `requires_match_columns` is still true for those files.
 */
export function getReimportBlockEnabled(
  analysisResult: CsvImport['analysis_result']
): boolean {
  const flaggedSummaries = Object.values(
    analysisResult?.file_summaries ?? {}
  ).filter(summary => summary.requires_match_columns);
  if (flaggedSummaries.length === 0) {
    return true;
  }
  return !flaggedSummaries.every(
    summary => summary.reimport_block_enabled === false
  );
}

export interface RowData {
  id: string;
  file_id: string;
  row_index: number;
  data: Record<string, unknown>;
  error?: string;
  /** Property names that caused validation errors (from analyzer error objects). */
  errorProperties?: string[];
  /** Raw per-property error details from the analyzer, preserved for the AI prompt builder. */
  rawErrors?: Array<{ property_name: string; error: string }>;
  hasConflict?: boolean;
  existing_entity_id?: string;
  associationDisplay?: Array<{
    label: string;
    value: string;
    resolved: boolean;
  }>;
}

// ============================================================================
// Constants
// ============================================================================

export interface CategoryConfig {
  title: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  color: 'green' | 'blue' | 'purple' | 'red';
  /**
   * Optional explanatory subtitle rendered under the section header. Used
   * to clarify categories whose intent isn't obvious from the title alone
   * (notably `batch_dedup`).
   */
  description?: string;
}

export const CATEGORY_CONFIGS: Record<RowCategory, CategoryConfig> = {
  create: { title: 'New records', icon: Plus, color: 'green' },
  update: { title: 'Updates', icon: RefreshCw01, color: 'blue' },
  batch_dedup: {
    title: 'Duplicates merged',
    icon: LayersThree01,
    color: 'purple',
    description:
      'Rows that shared a matching identifier (e.g. name or email) were automatically merged into a single record.',
  },
  invalid: { title: 'Invalid', icon: AlertCircle, color: 'red' },
  ambiguous: {
    title: 'Ambiguous match',
    icon: Users01,
    color: 'purple',
    description:
      'Multiple existing records matched these rows, so they were not imported. Add a more specific match column (e.g. a unique property) to resolve them.',
  },
};

export const COLOR_CLASSES = {
  green: {
    icon: 'text-utility-success-600',
    count: 'text-utility-success-700 dark:text-utility-success-400',
  },
  blue: {
    icon: 'text-utility-brand-600',
    count: 'text-utility-brand-700 dark:text-utility-brand-400',
  },
  purple: {
    icon: 'text-utility-gray-600',
    count: 'text-utility-gray-700 dark:text-utility-gray-400',
  },
  red: {
    icon: 'text-utility-error-600',
    count: 'text-utility-error-700 dark:text-utility-error-400',
  },
} as const;

export const SCHEMA_STALE_TIME = 5 * 60 * 1000;

/** Ordered list of row categories to render as accordion sections. */
export const CATEGORIES: RowCategory[] = [
  'create',
  'update',
  'batch_dedup',
  'invalid',
  'ambiguous',
];

/**
 * Max columns to display in a single table before silently truncating. The
 * table card has horizontal scroll + edge fades (via TableCard.ScrollArea),
 * so a cap mostly exists to keep rendering predictable on absurdly wide CSV
 * exports. 30 covers HubSpot's all-contacts export (~25 columns) and other
 * typical CRM exports. Columns are picked in insertion order across the
 * first rows of the file, so this can hide later fields if the file is
 * wider than the cap — bump this number if users hit that.
 */
export const MAX_TABLE_COLUMNS = 30;

// ============================================================================
// Helpers
// ============================================================================

const EMPTY_SCHEMA_MAP: PropertySchemaMap = {};
const EMPTY_PROPERTY_LABELS: Record<string, string> = {};

/** Map one analyzed row (inline or from the /rows endpoint) to the RowData shape the summary
 *  tables render. `id` and `fileId` are supplied by the caller because the /rows output omits
 *  file_id (inline rows use `${fileId}-${row_index}`; a /rows page uses its array position).
 *  `schemaMap`/`newPropertyLabels` are optional: when the caller doesn't have schemas loaded
 *  yet, the property label still humanizes the raw property name rather than showing it as-is. */
export function mapAnalyzedRowToRowData(
  row: AnalyzedRow,
  id: string,
  fileId: string,
  schemaMap: PropertySchemaMap = EMPTY_SCHEMA_MAP,
  newPropertyLabels: Record<string, string> = EMPTY_PROPERTY_LABELS
): RowData {
  let error: string | undefined;
  let errorProperties: string[] | undefined;
  if (row.errors && row.errors.length > 0) {
    error = row.errors
      .map(
        e =>
          `${getPropertyLabel(e.property_name, schemaMap, newPropertyLabels)}: ${e.error}`
      )
      .join('; ');
    errorProperties = row.errors.map(e => e.property_name).filter(Boolean);
  }
  return {
    id,
    file_id: fileId,
    row_index: row.row_index,
    data: row.data,
    error,
    errorProperties,
    rawErrors: row.errors,
    hasConflict: row.conflicts ? row.conflicts.length > 0 : false,
    existing_entity_id: row.existing_entity_id,
    associationDisplay: row.association_display,
  };
}

export async function fetchPropertyTypes(
  orgId: number,
  entityTypeName: string
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/?entity_type_name=${encodeURIComponent(entityTypeName)}`;
  const response = await fetchWithDefaults(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error('Failed to fetch property types');
  }
  return response.json();
}

export function getPropertyLabel(
  propertyName: string,
  schemaMap: PropertySchemaMap,
  newPropertyLabels: Record<string, string>
): string {
  const pt = schemaMap[propertyName];
  if (pt?.label) {
    return pt.label;
  }
  const newLabel = newPropertyLabels[propertyName];
  if (newLabel) {
    return newLabel;
  }
  return propertyName
    .replace(/^(ahoy_|org_)/, '')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
